import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { persistQueryClient } from '@tanstack/react-query-persist-client'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data revalidates after this
      gcTime: 24 * 60 * 60 * 1000, // 24 hours for persistence
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
})

const persister = createAsyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: 'react-query-cache',
})

declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: QueryClient
  }
}

interface QueryProviderProps {
  children: ReactNode
}

export const QueryProvider = ({ children }: QueryProviderProps) => {
  const [isRestored, setIsRestored] = useState(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-underscore-dangle
      window.__TANSTACK_QUERY_CLIENT__ = queryClient

      const [, restorePromise] = persistQueryClient({
        queryClient,
        persister,
        maxAge: 24 * 60 * 60 * 1000, // 24 hours
        dehydrateOptions: {
          // Only persist queries that contain 'persisted' in their query key
          shouldDehydrateQuery: (query) => query.queryKey.includes('persisted'),
        },
      })

      restorePromise
        .then(() => {
          setIsRestored(true)
        })
        .catch(() => {
          // If restoration fails, still allow the app to render
          setIsRestored(true)
        })
    } else {
      // On server side, skip restoration
      setIsRestored(true)
    }
  }, [])

  if (!isRestored) {
    return null
  }

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NEXT_PUBLIC_APP_ENV === 'local' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  )
}
